/**
 * The translator `frontOfficeView.ts` takes when a pure view-model function
 * produces reader-facing words — the same shape Admin's `model/translate.ts`
 * defines. A screen passes react-i18next's `t`; a caller that passes nothing
 * (a unit test, or a page that has not been updated yet) gets `english`, which
 * reads the same `en` trees the runtime registers, under the `cardDesk`,
 * `reports` and `hotelSettings` namespaces those keys already live in. The
 * English therefore lives in one place — the namespace file — and this module
 * never imports React or i18next, so `frontOfficeView.ts` stays a pure model.
 */
import { cardDesk } from '../../i18n/cardDesk';
import { reports } from '../../i18n/reports';
import { hotelSettings } from '../../i18n/hotelSettings';

export type Translate = (key: string, params?: Record<string, unknown>) => string;

const ENGLISH: Readonly<Record<string, unknown>> = {
  cardDesk: cardDesk.en,
  reports: reports.en,
  hotelSettings: hotelSettings.en,
};

function lookup(key: string): string | undefined {
  let node: unknown = ENGLISH;
  for (const segment of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

/**
 * i18next's behaviour for the English trees above: `count` picks `_one` or
 * `_other`, and `{{name}}` is replaced by the parameter. An unknown key reads
 * as itself, the way i18next shows a missing key.
 */
export const english: Translate = (key, params) => {
  const count = params?.count;
  const text = (typeof count === 'number' ? lookup(`${key}_${count === 1 ? 'one' : 'other'}`) : undefined)
    ?? lookup(key)
    ?? key;
  return text.replace(/\{\{\s*([^}\s]+)\s*\}\}/g, (_, name: string) => {
    const value = params?.[name];
    return value === undefined || value === null ? '' : String(value);
  });
};
